"use client";

import LocaleLink from "@/component/layout/LocaleLink";
import { useUnitsMap } from "@/hooks/useUnitsMap";
import { useCompare } from "@/lib/compare-store";
import type { Locale, Unit } from "@/types/units";
import { Plus } from "lucide-react";
import { useLocale } from "next-intl";
import CompareUnitCard from "./CompareUnitCard";

const COLS = "grid grid-cols-[minmax(110px,0.9fr)_repeat(3,1fr)]";
const labelCell =
  "flex items-center border-e border-black/10 bg-black/[0.03] p-6 dark:border-white/10 dark:bg-white/[0.03]";
const micro = "text-[11px] uppercase tracking-widest text-ash/50 dark:text-white/50";

/** Comparison table driven by the shared compare store + currency. */
export default function CompareTable() {
  const locale = useLocale() as Locale;
  const ar = locale === "ar";
  const compare = useCompare();
  const { map } = useUnitsMap();
  const units = compare.codes.map((c) => map.get(c)).filter(Boolean) as Unit[];
  const slots = Math.max(0, 3 - units.length);
  const nf = new Intl.NumberFormat(ar ? "ar-EG" : "en-US");

  const rows = [
    { key: "area", label: { ar: "المساحة", en: "Area" }, better: "max", val: (u: Unit) => u.area, fmt: (u: Unit) => `${nf.format(u.area)} ${ar ? "م²" : "m²"}` },
    { key: "rooms", label: { ar: "الغرف", en: "Rooms" }, better: "max", val: (u: Unit) => u.rooms, fmt: (u: Unit) => nf.format(u.rooms) },
    { key: "baths", label: { ar: "الحمامات", en: "Baths" }, better: "max", val: (u: Unit) => u.baths, fmt: (u: Unit) => nf.format(u.baths) },
    { key: "parking", label: { ar: "المواقف", en: "Parking" }, better: "max", val: (u: Unit) => u.parking, fmt: (u: Unit) => nf.format(u.parking) },
  ] as const;

  const bestOf = (r: (typeof rows)[number]) => {
    if (units.length < 2) return -1;
    let bi = 0;
    units.forEach((u, i) => {
      if (r.val(u) > r.val(units[bi])) bi = i;
    });
    return bi;
  };
  const statusLabel = (s: string) =>
    s === "available" ? (ar ? "متاحة" : "Available") : s === "reserved" ? (ar ? "محجوزة" : "Reserved") : (ar ? "مباعة" : "Sold");

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[760px] border border-black/10 dark:border-white/10">
        <div className={`${COLS} border-b border-black/10 dark:border-white/10`}>
          <div className={labelCell}>
            <span className={micro}>{ar ? "المواصفات" : "Specs"}</span>
          </div>
          {units.map((u) => (
            <CompareUnitCard key={u.code} unit={u} onRemove={() => compare.remove(u.code)} />
          ))}
          {Array.from({ length: slots }).map((_, i) => (
            <LocaleLink
              key={i}
              href="/properties"
              className="m-4 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-black/15 p-6 text-center opacity-60 transition-opacity hover:opacity-100 dark:border-white/15"
            >
              <Plus className="h-8 w-8" />
              <span className="text-sm font-semibold">{ar ? "أضف وحدة" : "Add unit"}</span>
              <span className="text-[11px] text-accent underline">{ar ? "تصفح المشاريع" : "Browse projects"}</span>
            </LocaleLink>
          ))}
        </div>

        {rows.map((r) => {
          const best = bestOf(r);
          return (
            <div key={r.key} className={`${COLS} border-b border-black/10 dark:border-white/10`}>
              <div className={labelCell}>
                <span className={micro}>{r.label[locale]}</span>
              </div>
              {units.map((u, i) => (
                <div key={u.code} className={`border-e border-black/10 p-6 dark:border-white/10 ${i === best ? "bg-accent/5 ring-1 ring-inset ring-accent/40" : ""}`}>
                  <span className={`text-lg font-semibold ${i === best ? "text-ash dark:text-white" : "text-ash/55 dark:text-white/55"}`}>{r.fmt(u)}</span>
                </div>
              ))}
              {Array.from({ length: slots }).map((_, i) => (
                <div key={i} className="p-6" />
              ))}
            </div>
          );
        })}

        <div className={COLS}>
          <div className={labelCell}>
            <span className={micro}>{ar ? "الحالة" : "Status"}</span>
          </div>
          {units.map((u) => (
            <div key={u.code} className="flex items-center gap-3 border-e border-black/10 p-6 dark:border-white/10">
              <span className={`h-3 w-3 ${u.status === "available" ? "bg-accent" : "bg-ash/30 dark:bg-white/30"}`} />
              <span className="text-lg font-semibold text-ash dark:text-white">{statusLabel(u.status)}</span>
            </div>
          ))}
          {Array.from({ length: slots }).map((_, i) => (
            <div key={i} className="p-6" />
          ))}
        </div>
      </div>
    </div>
  );
}
